import React, { useState } from 'react';
import {
  FileSpreadsheet,
  DollarSign,
  Users,
  Building,
  Briefcase,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Calendar,
  PieChart,
  BarChart3,
} from 'lucide-react';
import PLBenchmarkComparison from './PLBenchmarkComparison';
import { FinancialValue } from './shared/FinancialValue';
import { formatCurrency } from '../utils/financialFormat';

// Types matching backend CanonicalPL schema
interface PLLineItem {
  original_label: string;
  amount: number;
  category: string;
  expense_category?: string;
  confidence: number;
  requires_review: boolean;
}

interface PLPeriod {
  period_start: string;
  period_end: string;
  period_type: string;
  period_label?: string;
  
  // Summary totals
  total_revenue: number;
  total_cogs: number;
  gross_profit: number;
  total_operating_expenses: number;
  operating_income: number;
  net_income: number;
  
  // Phase 1.5: Granular expense breakdown
  officer_compensation: number;
  salaried_wages: number;
  hourly_wages: number;
  total_payroll: number;
  rent: number;
  utilities: number;
  insurance: number;
  professional_services: number;
  depreciation: number;
  debt_service: number;
  taxes_licenses: number;
  other_expenses_granular: number;
  
  // Line items
  revenue_items: PLLineItem[];
  cogs_items: PLLineItem[];
  expense_items: PLLineItem[];
}

interface ParsedPLStatement {
  source_file: string;
  source_format: string;
  parser_used: string;
  parsed_at: string;
  business_name?: string;
  periods: PLPeriod[];
  annual_summary?: {
    total_revenue: number;
    total_cogs: number;
    gross_profit: number;
    total_operating_expenses: number;
    net_income: number;
    officer_compensation: number;
    salaried_wages: number;
    hourly_wages: number;
    total_payroll: number;
    rent: number;
    utilities: number;
    insurance: number;
    professional_services: number;
    depreciation: number;
    debt_service: number;
    taxes_licenses: number;
    other_expenses_granular: number;
    period_count: number;
  };
  extraction_confidence: number;
  completeness_score: number;
  ready_for_future_closure: boolean;
}

interface ParsedPLDisplayProps {
  data: ParsedPLStatement;
  showDetails?: boolean;
  reportId?: string;  // For benchmark comparison
  businessType?: string;  // For benchmark comparison
}

const formatPercent = (value: number): string => {
  return `${(value * 100).toFixed(0)}%`;
};

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Category colors for expense breakdown
const EXPENSE_COLORS: Record<string, string> = {
  officer_compensation: 'var(--color-brand)',
  salaried_wages: 'var(--color-brand)',
  hourly_wages: 'var(--color-brand)',
  rent: 'var(--color-brand)',
  utilities: 'var(--color-brand)',
  insurance: 'var(--color-verified)',
  professional_services: 'var(--color-brand)',
  depreciation: 'var(--color-text-muted)',
  debt_service: 'var(--color-needs-human-text)',
  taxes_licenses: 'var(--color-brand)',
  other: 'var(--color-text-dim)',
};

const EXPENSE_LABELS: Record<string, string> = {
  officer_compensation: 'Officer Compensation',
  salaried_wages: 'Salaried Wages',
  hourly_wages: 'Hourly Wages',
  rent: 'Rent',
  utilities: 'Utilities',
  insurance: 'Insurance',
  professional_services: 'Professional Services',
  depreciation: 'Depreciation',
  debt_service: 'Debt Service',
  taxes_licenses: 'Taxes & Licenses',
  other_expenses_granular: 'Other Expenses',
};

export default function ParsedPLDisplay({ data, showDetails = true, reportId, businessType }: ParsedPLDisplayProps) {
  const [expandedPeriods, setExpandedPeriods] = useState<Set<number>>(new Set());
  const [showLineItems, setShowLineItems] = useState(false);
  const [showBenchmark, setShowBenchmark] = useState(false);

  const togglePeriod = (index: number) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedPeriods(newExpanded);
  };

  const summary = data.annual_summary;
  const confidenceColor = data.extraction_confidence >= 0.8 ? 'var(--color-verified)' :
                          data.extraction_confidence >= 0.6 ? 'var(--color-brand)' : 'var(--color-needs-human-text)';

  // Calculate expense breakdown for visualization
  const getExpenseBreakdown = (period: PLPeriod | typeof summary) => {
    if (!period) return [];
    const expenses = [
      { key: 'officer_compensation', value: period.officer_compensation || 0 },
      { key: 'salaried_wages', value: period.salaried_wages || 0 },
      { key: 'hourly_wages', value: period.hourly_wages || 0 },
      { key: 'rent', value: period.rent || 0 },
      { key: 'utilities', value: period.utilities || 0 },
      { key: 'insurance', value: period.insurance || 0 },
      { key: 'professional_services', value: period.professional_services || 0 },
      { key: 'depreciation', value: period.depreciation || 0 },
      { key: 'debt_service', value: period.debt_service || 0 },
      { key: 'taxes_licenses', value: period.taxes_licenses || 0 },
      { key: 'other_expenses_granular', value: period.other_expenses_granular || 0 },
    ].filter(e => e.value > 0);
    
    const total = expenses.reduce((sum, e) => sum + e.value, 0);
    return expenses.map(e => ({ ...e, percent: total > 0 ? (e.value / total) * 100 : 0 }));
  };

  return (
    <div className="rounded-lg border shadow-sm" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-needs-human-tint)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <FileSpreadsheet className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {data.business_name || 'Parsed P&L Statement'}
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {data.periods.length} period{data.periods.length !== 1 ? 's' : ''} •
                Parsed {formatDate(data.parsed_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {data.ready_for_future_closure ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                <CheckCircle className="w-4 h-4" />
                Ready for Analysis
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}>
                <AlertCircle className="w-4 h-4" />
                Needs Review
              </span>
            )}
            <div className="text-right">
              <div className="text-sm font-semibold" style={{ color: confidenceColor }}>
                {formatPercent(data.extraction_confidence)} Confidence
              </div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                via {data.parser_used.replace('_', ' ')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Annual Summary */}
      {summary && (
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
          <h4 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <TrendingUp className="w-4 h-4" />
            Annual Summary ({summary.period_count} periods)
          </h4>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'transparent' }}>
              <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-verified)' }}>Total Revenue</div>
              <div className="text-xl font-bold" style={{ color: 'var(--color-verified)' }}><FinancialValue value={summary.total_revenue} /></div>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-brand)' }}>Cost of Goods</div>
              <div className="text-xl font-bold" style={{ color: 'var(--color-brand)' }}><FinancialValue value={summary.total_cogs} /></div>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-needs-human-text)' }}>Operating Expenses</div>
              <div className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={summary.total_operating_expenses} /></div>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: summary.net_income >= 0 ? 'transparent' : 'var(--color-needs-human-tint)' }}>
              <div className="text-xs font-medium mb-1" style={{ color: summary.net_income >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                Net Income
              </div>
              <div className="text-xl font-bold" style={{ color: summary.net_income >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                {formatCurrency(summary.net_income)}
              </div>
            </div>
          </div>

          {/* Payroll Breakdown (Phase 1.5) */}
          <div className="mb-6">
            <h5 className="text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-label)' }}>
              <Users className="w-4 h-4" />
              Payroll Breakdown (3-Way Split)
            </h5>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <div className="text-xs font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Officer Compensation</div>
                <div className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={summary.officer_compensation} /></div>
                <div className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                  {summary.total_payroll > 0 ? `${((summary.officer_compensation || 0 / summary.total_payroll) * 100).toFixed(0)}% of payroll` : '-'}
                </div>
              </div>
              <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <div className="text-xs font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Salaried Wages</div>
                <div className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={summary.salaried_wages} /></div>
                <div className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                  {summary.total_payroll > 0 ? `${((summary.salaried_wages || 0 / summary.total_payroll) * 100).toFixed(0)}% of payroll` : '-'}
                </div>
              </div>
              <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <div className="text-xs font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Hourly Wages</div>
                <div className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={summary.hourly_wages} /></div>
                <div className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                  {summary.total_payroll > 0 ? `${((summary.hourly_wages || 0 / summary.total_payroll) * 100).toFixed(0)}% of payroll` : '-'}
                </div>
              </div>
            </div>
            <div className="mt-2 text-right text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Total Payroll: <span className="font-semibold"><FinancialValue value={summary.total_payroll} /></span>
            </div>
          </div>

          {/* Expense Breakdown Visual */}
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-label)' }}>
              <PieChart className="w-4 h-4" />
              Expense Categories
            </h5>
            <div className="space-y-2">
              {getExpenseBreakdown(summary).map(({ key, value, percent }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-32 text-xs truncate" style={{ color: 'var(--color-text-secondary)' }}>
                    {EXPENSE_LABELS[key] || key}
                  </div>
                  <div className="flex-1 h-4 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${percent}%`, backgroundColor: EXPENSE_COLORS[key] || 'var(--color-text-dim)' }}
                    />
                  </div>
                  <div className="w-24 text-right text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                    <FinancialValue value={value} />
                  </div>
                  <div className="w-12 text-right text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {percent.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Period Details */}
      {showDetails && data.periods.length > 0 && (
        <div className="px-6 py-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Calendar className="w-4 h-4" />
            Period Details
          </h4>
          <div className="space-y-2">
            {data.periods.map((period, index) => (
              <div key={index} className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                {/* Period Header */}
                <button
                  onClick={() => togglePeriod(index)}
                  className="w-full px-4 py-3 flex items-center justify-between transition-colors"
                  style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                >
                  <div className="flex items-center gap-3">
                    {expandedPeriods.has(index) ? (
                      <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    ) : (
                      <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    )}
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {period.period_label || `${period.period_start} - ${period.period_end}`}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)' }}>
                      {period.period_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-medium" style={{ color: 'var(--color-verified)' }}>
                      Revenue: <FinancialValue value={period.total_revenue} />
                    </span>
                    <span style={{ color: period.net_income >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                      Net: {formatCurrency(period.net_income)}
                    </span>
                  </div>
                </button>

                {/* Period Expanded Content */}
                {expandedPeriods.has(index) && (
                  <div className="px-4 py-4 border-t" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                    {/* Period Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Revenue</div>
                        <div className="font-semibold" style={{ color: 'var(--color-verified)' }}><FinancialValue value={period.total_revenue} /></div>
                      </div>
                      <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>COGS</div>
                        <div className="font-semibold" style={{ color: 'var(--color-brand)' }}><FinancialValue value={period.total_cogs} /></div>
                      </div>
                      <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Gross Profit</div>
                        <div className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={period.gross_profit} /></div>
                      </div>
                      <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Net Income</div>
                        <div className="font-semibold" style={{ color: period.net_income >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                          {formatCurrency(period.net_income)}
                        </div>
                      </div>
                    </div>

                    {/* Payroll Split */}
                    <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <div className="text-xs font-medium mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Payroll Split</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span style={{ color: 'var(--color-text-secondary)' }}>Officer:</span>{' '}
                          <span className="font-medium"><FinancialValue value={period.officer_compensation} /></span>
                        </div>
                        <div>
                          <span style={{ color: 'var(--color-text-secondary)' }}>Salaried:</span>{' '}
                          <span className="font-medium"><FinancialValue value={period.salaried_wages} /></span>
                        </div>
                        <div>
                          <span style={{ color: 'var(--color-text-secondary)' }}>Hourly:</span>{' '}
                          <span className="font-medium"><FinancialValue value={period.hourly_wages} /></span>
                        </div>
                      </div>
                    </div>

                    {/* Fixed Costs */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-2 rounded text-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <div className="text-xs" style={{ color: 'var(--color-brand)' }}>Rent</div>
                        <div className="font-medium" style={{ color: 'var(--color-brand)' }}><FinancialValue value={period.rent} /></div>
                      </div>
                      <div className="p-2 rounded text-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <div className="text-xs" style={{ color: 'var(--color-brand)' }}>Utilities</div>
                        <div className="font-medium" style={{ color: 'var(--color-brand)' }}><FinancialValue value={period.utilities} /></div>
                      </div>
                      <div className="p-2 rounded text-center" style={{ backgroundColor: 'transparent' }}>
                        <div className="text-xs" style={{ color: 'var(--color-verified)' }}>Insurance</div>
                        <div className="font-medium" style={{ color: 'var(--color-verified)' }}><FinancialValue value={period.insurance} /></div>
                      </div>
                    </div>

                    {/* Line Items Toggle */}
                    {(period.revenue_items?.length > 0 || period.expense_items?.length > 0) && (
                      <button
                        onClick={() => setShowLineItems(!showLineItems)}
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-needs-human-text)' }}
                      >
                        {showLineItems ? 'Hide' : 'Show'} Line Items
                      </button>
                    )}

                    {/* Line Items Tables */}
                    {showLineItems && (
                      <div className="mt-4 space-y-4">
                        {/* Revenue Items */}
                        {period.revenue_items?.length > 0 && (
                          <div>
                            <h6 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Revenue Items</h6>
                            <table className="w-full text-sm">
                              <tbody>
                                {period.revenue_items.map((item, i) => (
                                  <tr key={i} className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                                    <td className="py-1.5" style={{ color: 'var(--color-text-secondary)' }}>{item.original_label}</td>
                                    <td className="py-1.5 text-right font-medium" style={{ color: 'var(--color-verified)' }}>
                                      <FinancialValue value={item.amount} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Expense Items */}
                        {period.expense_items?.length > 0 && (
                          <div>
                            <h6 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Expense Items</h6>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                                  <th className="text-left py-1.5 font-medium" style={{ color: 'var(--color-text-muted)' }}>Item</th>
                                  <th className="text-left py-1.5 font-medium" style={{ color: 'var(--color-text-muted)' }}>Category</th>
                                  <th className="text-right py-1.5 font-medium" style={{ color: 'var(--color-text-muted)' }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {period.expense_items.map((item, i) => (
                                  <tr key={i} className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                                    <td className="py-1.5" style={{ color: 'var(--color-text-secondary)' }}>{item.original_label}</td>
                                    <td className="py-1.5">
                                      <span className="px-2 py-0.5 text-xs rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: EXPENSE_COLORS[item.expense_category || ''] || 'var(--color-text-secondary)' }}>
                                        {EXPENSE_LABELS[item.expense_category || ''] || item.expense_category || item.category}
                                      </span>
                                    </td>
                                    <td className="py-1.5 text-right font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                      <FinancialValue value={item.amount} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benchmark Comparison Section */}
      {reportId && showDetails && (
        <div className="px-6 py-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={() => setShowBenchmark(!showBenchmark)}
            className="w-full flex items-center justify-between py-2"
          >
            <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-medium">Industry Benchmark Comparison</span>
            </div>
            <span className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{showBenchmark ? 'Hide' : 'Show'}</span>
          </button>
          
          {showBenchmark && (
            <div className="mt-4">
              <PLBenchmarkComparison reportId={reportId} businessType={businessType} />
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-3 border-t flex items-center justify-between text-xs" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
        <span>
          Source: {data.source_file} ({data.source_format.toUpperCase()})
        </span>
        <span>
          Completeness: {formatPercent(data.completeness_score)}
        </span>
      </div>
    </div>
  );
}
